import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Col, Form, Row, Space, Spin } from 'antd';
import {
  useReportValuesDispatchContext,
  useReportValuesStateContext
} from 'providers/ReportValues';
import { useAppDispatch } from 'store/hooks';
import { setTour } from 'store/tour/slice';
import { fetchProfile } from 'services/api/profile';

import { CKEditor } from 'components/shared';
import { Content } from './styled';
import { Loading } from 'components/ui';
import { pickLocale } from 'utils/pickLocale';

interface IProps {
  loading: boolean;
  onSave: () => void;
  onSaveDraft: () => void;
  onClickBack: () => void;
}

const DoctorInfo: FC<IProps> = ({
  loading,
  onSave,
  onSaveDraft,
  onClickBack
}) => {
  const { doctor_info, lang } = useReportValuesStateContext();
  const [isLoading, setLoading] = useState(true);
  const { setValue } = useReportValuesDispatchContext();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (doctor_info) {
      setLoading(false);
      setTimeout(() => {
        dispatch(setTour({ key: 'addDoctorReport', value: true }));
      });
    } else {
      // profile.about is a locale-map jsonb on the v2 wire — pick the
      // report's DATA locale
      fetchProfile(lang || undefined)
        .then(profile => {
          setValue('doctor_info', pickLocale(profile.about, lang));
        })
        .catch(() => {
          // editor starts empty, same as a profile without about
        })
        .finally(() => {
          setLoading(false);
          setTimeout(() => {
            dispatch(setTour({ key: 'addDoctorReport', value: true }));
          });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTextChange = (value: string) => {
    setValue('doctor_info', value);
  };

  const onSubmit = (cb: () => void) => {
    if (doctor_info) {
      cb();
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Content>
      <div style={{ height: '100%', paddingBottom: 24 }}>
        <Form layout="vertical">
          <div>
            <Form.Item label={t('reports.about_doctor')}>
              <CKEditor
                onChange={handleTextChange}
                value={doctor_info || ''}
                toolbar={{
                  removeItems: [
                    'link',
                    'blockQuote',
                    'insertTable',
                    'mediaEmbed'
                  ]
                }}
              />
            </Form.Item>
          </div>
        </Form>
      </div>
      <Row justify="space-between" align="bottom">
        <Col>
          <Button onClick={onClickBack} disabled={loading}>
            {t('reports.back')}
          </Button>
        </Col>
        <Col>
          {loading ? (
            <Spin />
          ) : (
            <Space
              style={{
                paddingTop: 36,
                justifyContent: 'flex-end',
                width: '100%'
              }}
            >
              <Button
                ghost
                type="primary"
                disabled={!doctor_info}
                onClick={() => onSubmit(onSaveDraft)}
              >
                {t('reports.save')}
              </Button>
              <Button
                type="primary"
                disabled={!doctor_info}
                onClick={() => onSubmit(onSave)}
              >
                {t('reports.send')}
              </Button>
            </Space>
          )}
        </Col>
      </Row>
    </Content>
  );
};

export default DoctorInfo;
